import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  format,
  parseISO,
  startOfDay,
  addDays,
  addMonths,
  startOfMonth,
  getDay,
  getDaysInMonth,
  isSameDay,
  isSameMonth,
  differenceInMinutes,
  isWithinInterval,
} from 'date-fns';
import api from '../../../../api';
import './human-task-calendar.scss';

interface CalendarTime {
  dateTime?: string;
  date?: string;
  timeZone?: string;
}

interface CalendarEvent {
  id: string;
  summary: string;
  description: string | null;
  start: CalendarTime;
  end: CalendarTime;
  location: string | null;
  attendees: Array<{
    email: string;
    displayName?: string;
    responseStatus?: string;
  }>;
  htmlLink: string | null;
  isAllDay: boolean;
  highlighted: boolean;
}

interface CalendarData {
  success: boolean;
  events: CalendarEvent[];
  windowStart: string;
  windowEnd: string;
  highlightedEventIds: string[];
  calendarTimeZone: string | null;
  noCalendar?: boolean;
  message?: string;
}

interface HumanTaskCalendarProps {
  taskId: string;
}

const HOUR_HEIGHT = 48; // pixels per hour
const DAY_START_HOUR = 0; // midnight
const DAY_END_HOUR = 24; // midnight (full 24h)
const TOTAL_HOURS = DAY_END_HOUR - DAY_START_HOUR;
const DEFAULT_SCROLL_HOUR = 8; // auto-scroll to 8 AM on load

function getEventPosition(event: CalendarEvent, dayDate: Date) {
  if (event.isAllDay) return null;

  const start = parseISO(event.start.dateTime!);
  const end = parseISO(event.end.dateTime!);
  const dayStart = new Date(dayDate);
  dayStart.setHours(DAY_START_HOUR, 0, 0, 0);
  const dayEnd = new Date(dayDate);
  dayEnd.setHours(DAY_END_HOUR, 0, 0, 0);

  // Clamp to visible range
  const visibleStart = start < dayStart ? dayStart : start;
  const visibleEnd = end > dayEnd ? dayEnd : end;

  if (visibleStart >= dayEnd || visibleEnd <= dayStart) return null;

  const topMinutes = differenceInMinutes(visibleStart, dayStart);
  const durationMinutes = differenceInMinutes(visibleEnd, visibleStart);

  return {
    top: (topMinutes / 60) * HOUR_HEIGHT,
    height: Math.max((durationMinutes / 60) * HOUR_HEIGHT, 20), // min 20px
  };
}

function getEventsForDay(events: CalendarEvent[], dayDate: Date) {
  return events.filter((event) => {
    if (event.isAllDay) {
      const eventDate = parseISO(event.start.date!);
      return isSameDay(eventDate, dayDate);
    }
    const start = parseISO(event.start.dateTime!);
    const end = parseISO(event.end.dateTime!);
    const dayStart = new Date(dayDate);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(dayDate);
    dayEnd.setHours(23, 59, 59, 999);
    return (
      isWithinInterval(start, { start: dayStart, end: dayEnd }) ||
      isWithinInterval(end, { start: dayStart, end: dayEnd }) ||
      (start <= dayStart && end >= dayEnd)
    );
  });
}

const HumanTaskCalendar: React.FC<HumanTaskCalendarProps> = ({ taskId }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [data, setData] = useState<CalendarData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [expandedEventId, setExpandedEventId] = useState<string | null>(null);
  const gridContainerRef = useRef<HTMLDivElement>(null);
  const hasAutoScrolled = useRef(false);

  const fetchCalendar = useCallback(
    async (date?: string) => {
      try {
        setLoading(true);
        setError(null);
        const token = await getAccessTokenSilently();
        const params = date ? `?date=${date}` : '';
        const res = await api.get(`/human-tasks/${taskId}/calendar${params}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data.success) {
          setData(res.data);
        } else {
          setError('Failed to load calendar');
        }
      } catch (err: unknown) {
        const axiosErr = err as { response?: { data?: { error?: string } } };
        setError(axiosErr?.response?.data?.error || 'Failed to load calendar');
      } finally {
        setLoading(false);
      }
    },
    [taskId, getAccessTokenSilently],
  );

  // Initial load
  useEffect(() => {
    fetchCalendar();
  }, [fetchCalendar]);

  // Track which month is displayed in the mini-calendar
  const [viewMonth, setViewMonth] = useState<Date>(new Date());

  // Build the month grid days (Mon-Sun layout)
  const monthDays = useMemo(() => {
    const monthStart = startOfMonth(viewMonth);
    const startDow = getDay(monthStart); // 0 = Sun
    // Convert to Mon=0 based: (dow + 6) % 7
    const mondayOffset = (startDow + 6) % 7;
    const daysInMonth = getDaysInMonth(viewMonth);
    const result: (Date | null)[] = [];
    // Leading blanks
    for (let i = 0; i < mondayOffset; i++) result.push(null);
    // Actual days
    for (let i = 0; i < daysInMonth; i++) result.push(addDays(monthStart, i));
    return result;
  }, [viewMonth]);

  // Derive the 7-day event window from the API response
  const days = useMemo(() => {
    if (!data) return [];
    const start = startOfDay(parseISO(data.windowStart));
    const result: Date[] = [];
    for (let i = 0; i < 7; i++) {
      result.push(addDays(start, i));
    }
    return result;
  }, [data]);

  // Default to first day with a highlighted event, or today — only on initial load
  const hasInitializedSelection = useRef(false);
  useEffect(() => {
    if (!data || days.length === 0 || hasInitializedSelection.current) return;
    hasInitializedSelection.current = true;

    const highlightedEvent = data.events.find((e) => e.highlighted);
    if (highlightedEvent) {
      const eventDate = highlightedEvent.isAllDay
        ? parseISO(highlightedEvent.start.date!)
        : parseISO(highlightedEvent.start.dateTime!);
      const matchingDay = days.find((d) => isSameDay(d, eventDate));
      if (matchingDay) {
        setSelectedDate(matchingDay);
        setViewMonth(matchingDay);
        return;
      }
    }

    // Default to today if it's in the window, else first day
    const today = new Date();
    const todayInWindow = days.find((d) => isSameDay(d, today));
    const initial = todayInWindow || days[0];
    setSelectedDate(initial);
    setViewMonth(initial);
  }, [data, days]);

  // Auto-scroll to first event or DEFAULT_SCROLL_HOUR on initial load
  useEffect(() => {
    if (!data || hasAutoScrolled.current || !gridContainerRef.current) return;
    hasAutoScrolled.current = true;

    // Find first timed event of the selected day to scroll to
    const firstTimedEvent = data.events.find(
      (e) =>
        !e.isAllDay &&
        e.start.dateTime &&
        isSameDay(parseISO(e.start.dateTime), selectedDate),
    );

    let scrollHour = DEFAULT_SCROLL_HOUR;
    if (firstTimedEvent?.start.dateTime) {
      const eventHour = parseISO(firstTimedEvent.start.dateTime).getHours();
      scrollHour = Math.max(0, eventHour - 1); // 1 hour before first event
    }

    const scrollTop = (scrollHour - DAY_START_HOUR) * HOUR_HEIGHT;
    gridContainerRef.current.scrollTop = Math.max(0, scrollTop);
  }, [data, selectedDate]);

  const navigateMonth = (direction: -1 | 1) => {
    setViewMonth((prev) => addMonths(prev, direction));
  };

  const selectDay = (day: Date) => {
    setSelectedDate(day);
    // Fetch new data if the day is outside the current event window
    if (
      !data ||
      day < parseISO(data.windowStart) ||
      day > parseISO(data.windowEnd)
    ) {
      fetchCalendar(format(day, 'yyyy-MM-dd'));
    }
  };

  // 3-day view: previous day, selected day, next day
  const visibleDays = useMemo(() => {
    return [addDays(selectedDate, -1), selectedDate, addDays(selectedDate, 1)];
  }, [selectedDate]);

  const dayEventsMap = useMemo(() => {
    if (!data) return new Map<string, CalendarEvent[]>();
    const map = new Map<string, CalendarEvent[]>();
    for (const day of visibleDays) {
      map.set(day.toISOString(), getEventsForDay(data.events, day));
    }
    return map;
  }, [data, visibleDays]);

  if (loading && !data) {
    return (
      <div className='human-task-calendar loading'>
        <p>Loading calendar...</p>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className='human-task-calendar error'>
        <p>{error}</p>
      </div>
    );
  }

  if (data?.noCalendar) {
    return (
      <div className='human-task-calendar no-calendar'>
        <p>{data.message || 'No calendar connected.'}</p>
      </div>
    );
  }

  const hours = Array.from(
    { length: TOTAL_HOURS },
    (_, i) => i + DAY_START_HOUR,
  );

  return (
    <div className='human-task-calendar'>
      {/* Month calendar navigation */}
      <div className='month-nav'>
        <button
          className='nav-btn'
          onClick={() => navigateMonth(-1)}
          title='Previous month'
        >
          ‹
        </button>
        <span className='month-label'>{format(viewMonth, 'MMMM yyyy')}</span>
        <button
          className='nav-btn'
          onClick={() => navigateMonth(1)}
          title='Next month'
        >
          ›
        </button>
      </div>

      {/* Mini month grid */}
      <div className='month-grid'>
        {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map((d) => (
          <div key={d} className='month-grid-dow'>
            {d}
          </div>
        ))}
        {monthDays.map((day, idx) => {
          if (!day)
            return <div key={`blank-${idx}`} className='month-grid-blank' />;
          const isSelected = isSameDay(day, selectedDate);
          const isToday = isSameDay(day, new Date());
          const hasHighlighted = data?.events.some(
            (e) =>
              e.highlighted &&
              (e.isAllDay
                ? isSameDay(parseISO(e.start.date!), day)
                : e.start.dateTime &&
                  isSameDay(parseISO(e.start.dateTime), day)),
          );
          const hasEvent = data?.events.some((e) =>
            e.isAllDay
              ? isSameDay(parseISO(e.start.date!), day)
              : e.start.dateTime && isSameDay(parseISO(e.start.dateTime), day),
          );
          const inCurrentMonth = isSameMonth(day, viewMonth);

          return (
            <button
              key={day.toISOString()}
              className={[
                'month-grid-day',
                isSelected && 'selected',
                isToday && 'today',
                hasHighlighted && 'has-highlighted',
                hasEvent && !hasHighlighted && 'has-event',
                !inCurrentMonth && 'outside',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => selectDay(day)}
            >
              {format(day, 'd')}
            </button>
          );
        })}
      </div>

      {/* Timezone badge */}
      <div className='date-heading'>
        {data?.calendarTimeZone && (
          <span className='timezone-badge'>
            🕐 {data.calendarTimeZone.replace(/_/g, ' ')}
          </span>
        )}
        {loading && <span className='loading-indicator'>Updating...</span>}
      </div>

      {/* Sticky day column headers (above scroll area) */}
      <div className='day-headers-row'>
        <div className='hour-gutter-spacer' />
        {visibleDays.map((day) => {
          const isCenter = isSameDay(day, selectedDate);
          const isToday = isSameDay(day, new Date());
          return (
            <div
              key={day.toISOString()}
              className={[
                'day-column-header',
                isCenter && 'center-day',
                isToday && 'today',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <span className='col-day-name'>{format(day, 'EEE')}</span>
              <span className='col-day-number'>{format(day, 'd MMM')}</span>
            </div>
          );
        })}
      </div>

      {/* Scrollable time grid (midnight to midnight, default scroll to 8am) */}
      <div className='time-grid-container' ref={gridContainerRef}>
        <div className='three-day-grid'>
          {/* Hour labels column */}
          <div className='hour-labels-column'>
            {hours.map((hour) => (
              <div
                key={hour}
                className='hour-label-row'
                style={{ height: HOUR_HEIGHT }}
              >
                <span className='hour-label'>
                  {format(new Date(2000, 0, 1, hour), 'ha')}
                </span>
              </div>
            ))}
          </div>

          {/* Day columns (body only, headers are above) */}
          {visibleDays.map((day) => {
            const events = dayEventsMap.get(day.toISOString()) || [];
            const allDayEvts = events.filter((e) => e.isAllDay);
            const timedEvts = events.filter((e) => !e.isAllDay);
            const isCenter = isSameDay(day, selectedDate);
            const isToday = isSameDay(day, new Date());

            return (
              <div
                key={day.toISOString()}
                className={[
                  'day-column',
                  isCenter && 'center-day',
                  isToday && 'today',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                {/* All-day events for this column */}
                {allDayEvts.length > 0 && (
                  <div className='col-all-day'>
                    {allDayEvts.map((event) => (
                      <div
                        key={event.id}
                        className={`all-day-event ${event.highlighted ? 'highlighted' : 'unavailable'}`}
                      >
                        <span className='event-summary'>{event.summary}</span>
                        {event.location && (
                          <span className='event-location'>
                            {' '}
                            📍 {event.location}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Hour grid + events */}
                <div className='day-column-body'>
                  {hours.map((hour) => (
                    <div
                      key={hour}
                      className='hour-row'
                      style={{ height: HOUR_HEIGHT }}
                    >
                      <div className='hour-line' />
                    </div>
                  ))}

                  {/* Events overlay */}
                  <div className='events-overlay'>
                    {timedEvts.map((event) => {
                      const pos = getEventPosition(event, day);
                      if (!pos) return null;

                      return (
                        <div
                          key={event.id}
                          className={`time-event ${event.highlighted ? 'highlighted' : 'unavailable'}`}
                          style={{
                            top: pos.top,
                            height: pos.height,
                          }}
                          onClick={() =>
                            event.highlighted &&
                            setExpandedEventId(
                              expandedEventId === event.id ? null : event.id,
                            )
                          }
                        >
                          <div className='event-time'>
                            {format(parseISO(event.start.dateTime!), 'h:mm a')}
                            {' – '}
                            {format(parseISO(event.end.dateTime!), 'h:mm a')}
                          </div>
                          <div className='event-summary'>{event.summary}</div>
                          {event.location && (
                            <div className='event-location'>
                              📍 {event.location}
                            </div>
                          )}
                          {event.highlighted &&
                            expandedEventId === event.id && (
                              <EventDetails event={event} />
                            )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const EventDetails: React.FC<{ event: CalendarEvent }> = ({ event }) => (
  <div className='event-details'>
    {event.description && (
      <div className='detail-row'>
        <span className='detail-label'>Description</span>
        <span className='detail-value'>{event.description}</span>
      </div>
    )}
    {event.location && (
      <div className='detail-row'>
        <span className='detail-label'>Location</span>
        <span className='detail-value'>{event.location}</span>
      </div>
    )}
    {event.attendees.length > 0 && (
      <div className='detail-row'>
        <span className='detail-label'>Attendees</span>
        <span className='detail-value'>
          {event.attendees.map((a) => a.displayName || a.email).join(', ')}
        </span>
      </div>
    )}
  </div>
);

export default HumanTaskCalendar;
